import { Link, useParams, Navigate } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, Phone } from "lucide-react";
import { Reveal, MaskedLines, MonoLabel } from "../components/Reveal";
import Marquee from "../components/Marquee";
import QuoteForm from "../components/QuoteForm";
import { services, PHONE_DISPLAY, PHONE_TEL } from "../data/services";

const ServicePage = () => {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/" replace />;

  const idx = services.indexOf(service);
  const prev = services[(idx - 1 + services.length) % services.length];
  const next = services[(idx + 1) % services.length];

  return (
    <main>
      <section data-testid="service-hero" className="relative min-h-[70svh] flex items-end overflow-hidden bg-navy-deep noise-dark">
        <img
          src={service.image}
          alt={service.name}
          className={`absolute inset-0 h-full w-full ${
            service.contain ? "object-contain" : "object-cover"
          } object-center opacity-40`}
        />
        <div className="absolute inset-0 bg-navy-deep/50" />
        <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 w-full pt-40 pb-14">
          <MonoLabel light className="mb-6">{service.tag}</MonoLabel>
          <MaskedLines
            lines={[service.name.toUpperCase()]}
            className="font-display font-black uppercase tracking-tighter leading-[0.92] text-white text-[11vw] sm:text-[8vw] lg:text-[6.5rem]"
          />
          <p className="mt-8 max-w-2xl text-white/70 text-base sm:text-lg leading-relaxed font-medium">
            {service.blurb}
          </p>
        </div>
      </section>

      <Marquee
        dark
        items={[
          service.name,
          "pickup within 2 hours",
          "tracked door to door",
          `call ${PHONE_DISPLAY}`,
          "humans answer 24/7",
        ]}
      />

      <section data-testid="service-body" className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <MonoLabel>Overview</MonoLabel>
            {service.description.map((p, i) =>
              p.startsWith("## ") ? (
                <h3 key={i} className="mt-10 font-display text-2xl font-bold text-royal tracking-tight">
                  {p.slice(3)}
                </h3>
              ) : (
                <p key={i} className="mt-6 text-lg text-navy/70 leading-relaxed font-medium">
                  {p}
                </p>
              )
            )}

            <div className="mt-12 grid grid-cols-3 gap-px bg-blue-900/10 border border-blue-900/10">
              {service.stats.map((s) => (
                <div key={s.v} className="bg-white p-5 sm:p-7">
                  <p className="font-mono text-xl sm:text-3xl font-bold text-royal">{s.k}</p>
                  <p className="mt-1 font-mono text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-navy/45">
                    {s.v}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="border border-blue-900/10 bg-[#F5F8FD] p-8 sm:p-10 lg:sticky lg:top-28">
              <MonoLabel>What you get</MonoLabel>
              <ul className="mt-6 space-y-4">
                {service.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center bg-royal text-white">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span className="text-sm font-semibold text-navy/75 leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href={PHONE_TEL}
                data-testid="service-call-button"
                className="mt-10 flex items-center justify-center gap-3 h-14 bg-royal text-white font-display text-lg font-bold tracking-wide shadow-[5px_5px_0px_#040B16] hover:shadow-[2px_2px_0px_#040B16] hover:translate-x-[3px] hover:translate-y-[3px] transition-[box-shadow,transform] duration-150"
              >
                <Phone size={18} /> CALL {PHONE_DISPLAY}
              </a>
              <p className="mt-4 text-center font-mono text-[11px] tracking-widest uppercase text-navy/40">
                Immediate pricing on the first call
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="service-quote-section" className="py-24 bg-[#F5F8FD] border-t border-blue-900/10">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          <Reveal className="mb-12">
            <MonoLabel>Price this shipment</MonoLabel>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-[0.95] text-navy">
              Get a {service.name} quote
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <QuoteForm />
          </Reveal>
        </div>
      </section>

      <nav data-testid="service-pager" className="grid grid-cols-1 sm:grid-cols-2 border-t border-blue-900/10">
        <Link
          to={`/services/${prev.slug}`}
          data-testid="service-prev-link"
          className="group flex items-center gap-4 p-8 sm:p-12 border-b sm:border-b-0 sm:border-r border-blue-900/10 hover:bg-blue-50/60 transition-colors duration-300"
        >
          <ArrowLeft size={22} className="text-royal group-hover:-translate-x-2 transition-transform duration-300" />
          <span>
            <span className="block font-mono text-[11px] tracking-[0.2em] uppercase text-navy/40">Previous service</span>
            <span className="block font-display text-xl sm:text-2xl font-bold text-navy mt-1">{prev.name}</span>
          </span>
        </Link>
        <Link
          to={`/services/${next.slug}`}
          data-testid="service-next-link"
          className="group flex items-center justify-end gap-4 p-8 sm:p-12 text-right hover:bg-blue-50/60 transition-colors duration-300"
        >
          <span>
            <span className="block font-mono text-[11px] tracking-[0.2em] uppercase text-navy/40">Next service</span>
            <span className="block font-display text-xl sm:text-2xl font-bold text-navy mt-1">{next.name}</span>
          </span>
          <ArrowRight size={22} className="text-royal group-hover:translate-x-2 transition-transform duration-300" />
        </Link>
      </nav>
    </main>
  );
};

export default ServicePage;
